import { z } from 'zod';
const IS_GENERATING_DOCS = typeof process !== 'undefined' && process.env?.GENERATE_DOCS === 'true';
function registerDocumentSchema(schema, refId) {
    if (!IS_GENERATING_DOCS)
        return schema;
    return schema.meta({ id: refId });
}
/**
 * BUSINESS SEGMENT CONDITIONS SYSTEM
 * ===================================
 *
 * This file defines the types for business segment conditions - the rules that determine
 * which users belong to a segment. The most confusing part is the relative date system.
 *
 * RELATIVE DATE SYSTEM EXPLAINED:
 * ===============================
 *
 * Relative dates are calculated from "today" and can go backwards (BEFORE) or forwards (AFTER).
 *
 * BASIC CONCEPTS:
 * - BEFORE = "X days before today" = today - X days (past date)
 * - AFTER = "X days after today" = today + X days (future date)
 *
 * OPERATOR COMBINATIONS:
 * ======================
 *
 * 1. LESS_THAN + BEFORE = "date < (today - X days)" = "date is older than X days ago"
 *    Example: Find users who booked more than 180 days ago
 *    { value: 180, unit: 'days', direction: 'before', operator: 'less_than' }
 *
 * 2. GREATER_THAN + BEFORE = "date > (today - X days)" = "date is more recent than X days ago"
 *    Example: Find users who booked within the last 30 days
 *    { value: 30, unit: 'days', direction: 'before', operator: 'greater_than' }
 *
 * 3. LESS_THAN + AFTER = "date < (today + X days)" = "date is before X days from now"
 *    Example: Find users with bookings before next month
 *    { value: 30, unit: 'days', direction: 'after', operator: 'less_than' }
 *
 * 4. GREATER_THAN + AFTER = "date > (today + X days)" = "date is after X days from now"
 *    Example: Find users with future bookings more than 7 days away
 *    { value: 7, unit: 'days', direction: 'after', operator: 'greater_than' }
 *
 * COMMON BUSINESS USE CASES:
 * ==========================
 *
 * 1. BOARDING CROSS-SELL: "Users who had daycare more recently than 180 days ago but never booked boarding"
 *    - Daycare date: GREATER_THAN + BEFORE (date > today - 180 days)
 *    - Boarding bookings: EQUALS 0
 *
 * 2. RECENT CUSTOMERS: "Users who booked within the last 30 days"
 *    - Booking date: GREATER_THAN + BEFORE (date > today - 30 days)
 *
 * 3. FUTURE BOOKINGS: "Users with bookings more than 7 days in the future"
 *    - Booking date: GREATER_THAN + AFTER (date > today + 7 days)
 *
 * 4. OLD CUSTOMERS: "Users who haven't booked in over 90 days"
 *    - Last booking date: LESS_THAN + BEFORE (date < today - 90 days)
 *
 * DEBUGGING TIPS:
 * ===============
 *
 * 1. Always think: "What date am I comparing against?"
 * 2. BEFORE = past reference point, AFTER = future reference point
 * 3. LESS_THAN = "older than", GREATER_THAN = "more recent than"
 * 4. Test with concrete dates: "If today is Jan 1, 2024, what does 180 days BEFORE mean?"
 * 5. Remember: GREATER_THAN + BEFORE = "within the last X days", not "less than X days ago"
 *
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts (tenant AI) and @see partner-segment-generation.service.ts (partner AI prompt)
 */
/**
 * Units for relative date calculations
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts (tenant AI) and @see partner-segment-generation.service.ts (partner AI prompt)
 */
export var DateUnit;
(function (DateUnit) {
    DateUnit["DAYS"] = "days";
    DateUnit["MONTHS"] = "months";
    DateUnit["YEARS"] = "years";
})(DateUnit || (DateUnit = {}));
/**
 * Direction for relative date calculations
 *
 * ⚠️ CONFUSING LOGIC WARNING ⚠️
 *
 * The direction determines the reference point for the date calculation:
 *
 * BEFORE = "X days before today" = today - X days (past date)
 * AFTER = "X days after today" = today + X days (future date)
 *
 * Examples:
 * - { value: 30, unit: 'days', direction: 'before' } = 30 days ago
 * - { value: 30, unit: 'days', direction: 'after' } = 30 days from now
 *
 * When combined with operators:
 * - LESS_THAN + BEFORE = "date < (today - X days)" = "date is older than X days ago"
 * - GREATER_THAN + BEFORE = "date > (today - X days)" = "date is newer than X days ago"
 * - LESS_THAN + AFTER = "date < (today + X days)" = "date is before X days from now"
 * - GREATER_THAN + AFTER = "date > (today + X days)" = "date is after X days from now"
 *
 * Common use cases:
 * - "Users who booked more than 180 days ago": LESS_THAN + BEFORE
 * - "Users who booked less than 30 days ago": GREATER_THAN + BEFORE
 * - "Users with future bookings": GREATER_THAN + AFTER
 * - "Users with bookings before next month": LESS_THAN + AFTER
 */
export var RelativeDateDirection;
(function (RelativeDateDirection) {
    RelativeDateDirection["BEFORE"] = "before";
    RelativeDateDirection["AFTER"] = "after";
})(RelativeDateDirection || (RelativeDateDirection = {}));
/**
 * All reach built in potential fields for a condition in a segment
 */
export const ConditionFieldEnum = z.enum([
    'userId',
    'email',
    'phone',
    'firstName',
    'lastName',
    'hipaaConsent',
]);
export const sqlSegmentConditionSchema = z.object({
    sql_segment_id: z.string(),
});
/**
 * All potential operators for a condition in a segment
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts (tenant AI) and @see partner-segment-generation.service.ts (partner AI prompt)
 */
export var ConditionOperatorEnumType;
(function (ConditionOperatorEnumType) {
    ConditionOperatorEnumType["EQUALS"] = "equals";
    ConditionOperatorEnumType["NOT_EQUALS"] = "not_equals";
    ConditionOperatorEnumType["GREATER_THAN"] = "greater_than";
    ConditionOperatorEnumType["LESS_THAN"] = "less_than";
    ConditionOperatorEnumType["EXISTS"] = "exists";
    ConditionOperatorEnumType["NOT_EXISTS"] = "not_exists";
    /**
     * These operators are specific for date fields
     */
    ConditionOperatorEnumType["EQUALS_MONTH_DAY"] = "equals_month_day";
    ConditionOperatorEnumType["EQUALS_MONTH_DAY_YEAR"] = "equals_month_day_year";
    ConditionOperatorEnumType["BETWEEN_MONTH_DAY"] = "between_month_day";
    ConditionOperatorEnumType["BETWEEN_MONTH_DAY_YEAR"] = "between_month_day_year";
    /**
     * These operators are specific for array fields
     */
    ConditionOperatorEnumType["ARRAY_CONTAINS"] = "array_contains";
    /**
     * String pattern matching (substring search)
     */
    ConditionOperatorEnumType["CONTAINS"] = "contains";
    ConditionOperatorEnumType["NOT_CONTAINS"] = "not_contains";
    /**
     * IN: matches if the field value strictly equals one of the values in the array.
     * LHS can be any type (string, number, etc.); RHS must be an array of that same type.
     */
    ConditionOperatorEnumType["IN"] = "in";
})(ConditionOperatorEnumType || (ConditionOperatorEnumType = {}));
export const ConditionOperatorEnum = z.nativeEnum(ConditionOperatorEnumType);
/**
 * Relative distances compile to a Postgres `INTERVAL`, which rejects the whole query once the
 * resulting date leaves Postgres's supported range (4713 BC – 5874897 AD). These caps sit a
 * millennium out — far beyond any real segment — so an out-of-range distance fails validation on
 * save instead of breaking segment evaluation later.
 */
export const MAX_RELATIVE_DATE_VALUE_BY_UNIT = {
    [DateUnit.DAYS]: 365_000,
    [DateUnit.MONTHS]: 12_000,
    [DateUnit.YEARS]: 1_000,
};
/**
 * Structure for relative date values
 *
 * This creates a relative date calculation based on today's date.
 *
 * @example
 * // 30 days ago
 * { type: 'relative', value: 30, unit: 'days', direction: 'before' }
 *
 * @example
 * // 7 days from now
 * { type: 'relative', value: 7, unit: 'days', direction: 'after' }
 *
 * @example
 * // For boarding cross-sell: users who had daycare more than 180 days ago
 * {
 *   field: 'Daycare Invoice Period Start Date',
 *   value: [{ type: 'relative', value: 180, unit: 'days', direction: 'before' }],
 *   operator: 'less_than'  // date < (today - 180 days)
 * }
 *
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts (tenant AI) and @see partner-segment-generation.service.ts (partner AI prompt)
 */
export const relativeDateSchema = registerDocumentSchema(z
    .object({
    type: z.literal('relative'),
    value: z.number().int().nonnegative().describe('Number of date units to add/subtract from today'),
    unit: z.nativeEnum(DateUnit),
    direction: z.nativeEnum(RelativeDateDirection),
})
    .refine(relativeDate => relativeDate.value <= MAX_RELATIVE_DATE_VALUE_BY_UNIT[relativeDate.unit], {
    path: ['value'],
    message: 'Relative date distance is too large',
}), 'SegmentRelativeDate');
/**
 * Structure for between date values
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts (tenant AI) and @see partner-segment-generation.service.ts (partner AI prompt)
 */
export const betweenDateSchema = registerDocumentSchema(z.object({
    startDate: z.union([z.coerce.date(), relativeDateSchema]),
    endDate: z.union([z.coerce.date(), relativeDateSchema]),
}), 'SegmentBetweenDate');
/**
 * A single condition in a segment.
 * In condition groups, every item has a type for a discriminated union; field conditions use type: 'property'.
 * @NOTE ⚠️ Anytime you update this you HAVE to update @see textToSegment.ts (tenant AI) and @see partner-segment-generation.service.ts (partner AI prompt)
 */
const fieldConditionSchema = registerDocumentSchema(z.object({
    type: z.literal('property').optional(),
    field: z.union([ConditionFieldEnum, z.string()]),
    value: z
        .array(z.union([z.string(), z.number(), z.boolean(), z.date(), relativeDateSchema, betweenDateSchema]))
        .describe('The value to compare the field against (string, number, boolean, date)'),
    operator: ConditionOperatorEnum,
    description: z.string().optional().describe('Optional human-readable note describing this clause'),
}), 'SegmentFieldCondition');
/**
 * Operators for count-based quantifier comparisons.
 * Used to specify how to compare the count of matching related resources.
 *
 * Common patterns:
 * - "has any": countOperator: 'gte', countValue: 1
 * - "does not have": countOperator: 'eq', countValue: 0
 * - "has at least 3": countOperator: 'gte', countValue: 3
 * - "has exactly 2": countOperator: 'eq', countValue: 2
 */
export var CountOperator;
(function (CountOperator) {
    CountOperator["EQ"] = "eq";
    CountOperator["GT"] = "gt";
    CountOperator["GTE"] = "gte";
    CountOperator["LT"] = "lt";
    CountOperator["LTE"] = "lte";
})(CountOperator || (CountOperator = {}));
export var AggregateFunction;
(function (AggregateFunction) {
    AggregateFunction["SUM"] = "sum";
    AggregateFunction["AVG"] = "avg";
    AggregateFunction["MIN"] = "min";
    AggregateFunction["MAX"] = "max";
})(AggregateFunction || (AggregateFunction = {}));
const NUMERIC_ONLY_AGGREGATE_FUNCTIONS = new Set([
    AggregateFunction.SUM,
    AggregateFunction.AVG,
]);
export const relatedResourceAggregateSchema = registerDocumentSchema(z
    .object({
    function: z.nativeEnum(AggregateFunction),
    field: z.string().min(1),
    operator: z.nativeEnum(CountOperator),
    value: z.union([z.number(), relativeDateSchema]),
})
    .refine(aggregate => !NUMERIC_ONLY_AGGREGATE_FUNCTIONS.has(aggregate.function) ||
    typeof aggregate.value === 'number', { path: ['value'], message: 'sum and avg aggregates compare against a number' }), 'SegmentRelatedResourceAggregate');
export function compareWithCountOperator(args) {
    const { left, operator, right } = args;
    switch (operator) {
        case CountOperator.EQ:
            return left === right;
        case CountOperator.GT:
            return left > right;
        case CountOperator.GTE:
            return left >= right;
        case CountOperator.LT:
            return left < right;
        case CountOperator.LTE:
            return left <= right;
        default:
            throw new Error(`Unhandled CountOperator: ${operator}`);
    }
}
/**
 * A subject with no matching records has a count of 0 and a SUM of 0; AVG, MIN, and MAX
 * have no value, so their comparison never holds for it.
 */
export function aggregateConditionIsSatisfiedWithZeroMatches(condition, aggregate) {
    const { value } = aggregate;
    const isSum = aggregate.function === AggregateFunction.SUM;
    if (!isSum || typeof value !== 'number') {
        return false;
    }
    const countHoldsForZeroRecords = compareWithCountOperator({
        left: 0,
        operator: condition.countOperator,
        right: condition.countValue,
    });
    const sumHoldsForZeroRecords = compareWithCountOperator({
        left: 0,
        operator: aggregate.operator,
        right: value,
    });
    return countHoldsForZeroRecords && sumHoldsForZeroRecords;
}
/**
 * Which record owns the foreign-key field, relative to the record currently
 * being evaluated by this condition.
 *
 * - CURRENT: the current record points to the linked record.
 * - LINKED: the linked record points back to the current record.
 */
export var ForeignKeyOn;
(function (ForeignKeyOn) {
    ForeignKeyOn["CURRENT"] = "current";
    ForeignKeyOn["LINKED"] = "linked";
})(ForeignKeyOn || (ForeignKeyOn = {}));
export const relatedResourceSchema = registerDocumentSchema(z.object({
    schemaId: z.string(),
    foreignKeyPath: z.string(),
    foreignKeyOn: z.nativeEnum(ForeignKeyOn).optional(),
}), 'SegmentRelatedResource');
export function isForeignKeyOnCurrentResource(relatedResource) {
    return relatedResource.foreignKeyOn === ForeignKeyOn.CURRENT;
}
/** True when the linked record owns the foreign key, including legacy conditions. */
export function isForeignKeyOnLinkedResource(relatedResource) {
    return !isForeignKeyOnCurrentResource(relatedResource);
}
/** Determine where a relationship's foreign key lives relative to its subject. */
export function foreignKeyOnForRelationship(args) {
    const { relationship, subjectSchemaId } = args;
    if (relationship.sourceSchemaId === subjectSchemaId)
        return ForeignKeyOn.CURRENT;
    if (relationship.targetSchemaId === subjectSchemaId)
        return ForeignKeyOn.LINKED;
    throw new Error(`Relationship ${relationship.id} does not touch subject schema ${subjectSchemaId}`);
}
/** Return the schema at the other end of a relationship from its subject. */
export function relatedSchemaIdForRelationship(args) {
    const { relationship, subjectSchemaId } = args;
    const foreignKeyOn = foreignKeyOnForRelationship({ relationship, subjectSchemaId });
    return foreignKeyOn === ForeignKeyOn.LINKED
        ? relationship.sourceSchemaId
        : relationship.targetSchemaId;
}
/** Resolve a relationship from a subject record into its persisted condition values. */
export function getRelationshipDirection(args) {
    const { relationship, subjectSchemaId } = args;
    const foreignKeyOn = foreignKeyOnForRelationship({ relationship, subjectSchemaId });
    const linkedResourceOwnsForeignKey = foreignKeyOn === ForeignKeyOn.LINKED;
    const relatedSchemaId = linkedResourceOwnsForeignKey
        ? relationship.sourceSchemaId
        : relationship.targetSchemaId;
    return {
        foreignKeyOn,
        relatedSchemaId,
        linkedResourceOwnsForeignKey,
    };
}
/** Maximum relationship hops allowed in a segment condition. */
export const MAX_RELATED_RESOURCE_DEPTH = 4;
export const MAX_CONDITION_GROUP_DEPTH = 3;
const relatedResourceConditionBaseFields = {
    type: z.literal('related_resource'),
    relatedResource: relatedResourceSchema,
    countOperator: z.nativeEnum(CountOperator),
    countValue: z.number().int().min(0),
    aggregate: relatedResourceAggregateSchema.optional(),
    description: z.string().optional(),
};
/**
 * The `.meta({ id })` is load-bearing, not decoration: without a registered id the OpenAPI
 * generator inlines the self-reference until it exhausts its stack.
 *
 * Structural depth is unbounded here; MAX_RELATED_RESOURCE_DEPTH is enforced by the
 * condition-group depth refinement.
 */
export const relatedResourceConditionSchema = z
    .object({
    ...relatedResourceConditionBaseFields,
    get conditions() {
        return z.array(z.union([fieldConditionSchema, relatedResourceConditionSchema]));
    },
})
    .meta({
    id: 'RelatedResourceCondition',
    description: `Query the subject by its related resources. Nests at most ${MAX_RELATED_RESOURCE_DEPTH} relationship hops.`,
});
export function isRelatedResourceCondition(condition) {
    return (typeof condition === 'object' &&
        condition !== null &&
        'type' in condition &&
        condition.type === 'related_resource');
}
/**
 * Type guard for field (property) conditions.
 * Supports discriminated shape (type === 'property') and legacy shape ('field' in condition).
 */
export function isFieldCondition(condition) {
    if (typeof condition !== 'object' || condition === null)
        return false;
    const c = condition;
    if (c.type === 'property')
        return true;
    if (c.type === 'related_resource' || c.type === 'group')
        return false;
    return 'field' in c;
}
export function isSqlSegmentCondition(condition) {
    return 'sql_segment_id' in condition;
}
/**
 * Logic type for condition groups
 */
export var ConditionGroupLogic;
(function (ConditionGroupLogic) {
    ConditionGroupLogic["AND"] = "and";
    ConditionGroupLogic["OR"] = "or";
})(ConditionGroupLogic || (ConditionGroupLogic = {}));
/**
 * The self-reference must target this base schema; pointing it at the depth-refined export
 * exhausts the OpenAPI generator's stack.
 */
const conditionGroupBaseSchema = z
    .object({
    type: z.literal('group').optional().default('group'),
    logic: z.nativeEnum(ConditionGroupLogic),
    get conditions() {
        return z.array(z.union([fieldConditionSchema, relatedResourceConditionSchema, conditionGroupBaseSchema]));
    },
    description: z.string().optional(),
})
    .transform(val => ({ ...val, type: 'group' }))
    .meta({
    id: 'ConditionGroup',
    description: 'Conditions combined with AND/OR logic. Groups may nest to any depth.',
});
function seekDepthIssue(args) {
    const { item, path, groupDepth, relatedResourceDepth } = args;
    const nextGroupDepth = isConditionGroup(item) ? groupDepth + 1 : groupDepth;
    const nextRelatedResourceDepth = isRelatedResourceCondition(item)
        ? relatedResourceDepth + 1
        : relatedResourceDepth;
    if (nextGroupDepth > MAX_CONDITION_GROUP_DEPTH) {
        return {
            path,
            message: `Condition groups can be nested at most ${MAX_CONDITION_GROUP_DEPTH} levels deep; group at "${path.join('.')}" is at depth ${nextGroupDepth}`,
        };
    }
    if (nextRelatedResourceDepth > MAX_RELATED_RESOURCE_DEPTH) {
        return {
            path,
            message: `Related-resource conditions can be nested at most ${MAX_RELATED_RESOURCE_DEPTH} levels deep; condition at "${path.join('.')}" is at depth ${nextRelatedResourceDepth}`,
        };
    }
    if (!isConditionGroup(item) && !isRelatedResourceCondition(item))
        return undefined;
    for (const [index, child] of item.conditions.entries()) {
        const issue = seekDepthIssue({
            item: child,
            path: [...path, 'conditions', index],
            groupDepth: nextGroupDepth,
            relatedResourceDepth: nextRelatedResourceDepth,
        });
        if (issue)
            return issue;
    }
    return undefined;
}
export const conditionGroupSchema = conditionGroupBaseSchema.superRefine((group, ctx) => {
    const issue = seekDepthIssue({
        item: group,
        path: [],
        groupDepth: 0,
        relatedResourceDepth: 0,
    });
    if (issue)
        ctx.addIssue({ code: z.ZodIssueCode.custom, ...issue });
});
export const CLAUSE_DESCRIPTION_TRUNCATE_LENGTH = 300;
const CLAUSE_DESCRIPTION_REQUIRED = "Every group and condition needs a short plain-language description of what it matches, in the business's vocabulary rather than field or operator jargon.";
const clauseDescriptionSchema = z
    .string({ error: CLAUSE_DESCRIPTION_REQUIRED })
    .trim()
    .min(1, CLAUSE_DESCRIPTION_REQUIRED)
    .describe('A short plain-language label saying what this clause matches');
const describedFieldConditionSchema = fieldConditionSchema.extend({
    type: z.literal('property'),
    description: clauseDescriptionSchema,
});
function buildDescribedRelatedResourceSchema(maxHops) {
    const leafHopSchema = z.object({
        ...relatedResourceConditionBaseFields,
        description: clauseDescriptionSchema,
        conditions: z.array(describedFieldConditionSchema),
    });
    return Array.from({ length: Math.max(maxHops - 1, 0) }).reduce(deeperHopSchema => z.object({
        ...relatedResourceConditionBaseFields,
        description: clauseDescriptionSchema,
        conditions: z.array(z.discriminatedUnion('type', [describedFieldConditionSchema, deeperHopSchema])),
    }), leafHopSchema);
}
/** Recurses while building, so the resulting schema itself is fully expanded. */
function buildDescribedConditionGroupSchema(args) {
    const relatedResourceSchemaForDepth = buildDescribedRelatedResourceSchema(args.relatedResourceDepth);
    const buildGroupAtDepth = (remainingDepth) => {
        const nestableMembers = remainingDepth > 1 ? [buildGroupAtDepth(remainingDepth - 1)] : [];
        return z.object({
            type: z.literal('group'),
            logic: z.nativeEnum(ConditionGroupLogic),
            description: clauseDescriptionSchema,
            conditions: z
                .array(z.discriminatedUnion('type', [
                describedFieldConditionSchema,
                relatedResourceSchemaForDepth,
                ...nestableMembers,
            ]))
                .min(1),
        });
    };
    return buildGroupAtDepth(args.groupDepth);
}
/**
 * Kept separate from `conditionGroupSchema` on purpose: the REST API, tenant
 * text-to-segment, and stored SQL segments all author clauses without descriptions.
 */
export const describedConditionGroupSchema = buildDescribedConditionGroupSchema({
    groupDepth: MAX_CONDITION_GROUP_DEPTH,
    relatedResourceDepth: MAX_RELATED_RESOURCE_DEPTH,
});
export const describedSegmentConditionGroupsSchema = z.array(describedConditionGroupSchema).min(1);
/**
 * Type guard for condition groups.
 * Supports discriminated shape (type === 'group') and legacy shape (logic + conditions).
 */
export function isConditionGroup(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const v = value;
    if (v.type === 'group')
        return true;
    return 'logic' in v && 'conditions' in v && Array.isArray(v.conditions);
}
/**
 * The union of all managed fields (@see ConditionFieldEnum) + custom fields (string)
 */
export const ConditionSchema = z.union([
    fieldConditionSchema,
    sqlSegmentConditionSchema,
    relatedResourceConditionSchema,
]);
// Schema for arrays of field conditions specifically (no SQL segments)
export const fieldConditionsArraySchema = z.array(fieldConditionSchema);
/**
 * Segment condition groups format: array of groups (AND between groups).
 * Supports related-resource conditions and grouped AND/OR logic.
 */
export const segmentConditionGroupsSchema = z.array(conditionGroupSchema);
export const BusinessSegmentConditionsSchema = z.array(ConditionSchema);
//# sourceMappingURL=conditions.js.map